import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages mounted and animating state for modals / bottom sheets so that
 * exit animations (slide down, fade out) always play fully before unmounting,
 * regardless of whether the close was initiated internally (backdrop/X click)
 * or externally (parent toggling visible prop to false).
 */
export function useSheetLifecycle(
    visible: boolean,
    onClose?: () => void,
    durationMs: number = 220
) {
    const [isRendered, setIsRendered] = useState(visible);
    const [isClosing, setIsClosing] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const prevVisibleRef = useRef(visible);

    useEffect(() => {
        if (visible) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setIsRendered(true);
            setIsClosing(false);
        } else if (prevVisibleRef.current && !visible) {
            // Parent flipped visible to false externally
            setIsClosing(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                setIsRendered(false);
                setIsClosing(false);
            }, durationMs);
        }
        prevVisibleRef.current = visible;

        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [visible, durationMs]);

    const handleClose = useCallback(() => {
        if (isClosing) return;
        setIsClosing(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            setIsRendered(false);
            setIsClosing(false);
            if (onClose) {
                onClose();
            }
        }, durationMs);
    }, [isClosing, durationMs, onClose]);

    return {
        isRendered,
        isClosing,
        handleClose,
    };
}

export default useSheetLifecycle;
